import React, { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functionsInstance } from '../../../services/firebase/firebase-config';
import '../../../styles/email-collection-form.css';

const EmailCollectionForm = ({ onSubmissionSuccess, onError, initialData = {} }) => {
  const [formData, setFormData] = useState({
    email: initialData.email || '',
    linkedinUrl: initialData.linkedinUrl || ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const isValidLinkedInUrl = (url) => {
    const linkedinRegex = /^https?:\/\/(www\.)?linkedin\.com\/in\/[a-zA-Z0-9-]+\/?$/;
    return linkedinRegex.test(url);
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!formData.linkedinUrl.trim()) {
      errors.linkedinUrl = 'LinkedIn URL is required';
    } else if (!isValidLinkedInUrl(formData.linkedinUrl)) {
      errors.linkedinUrl = 'Please enter a valid LinkedIn URL';
    }

    return errors;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setIsSubmitting(true);
    
    try {
      const createEmailVerification = httpsCallable(functionsInstance, 'createEmailVerification');
      const result = await createEmailVerification({ 
        email: formData.email.trim(),
        linkedinUrl: formData.linkedinUrl.trim()
      });
      
      if (result.data.success) {
        onSubmissionSuccess({
          email: formData.email.trim(),
          linkedinUrl: formData.linkedinUrl.trim(),
          verificationId: result.data.verificationId
        });
      } else {
        onError(result.data.message || 'Failed to send verification code');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      
      let errorMessage = 'Failed to send verification code. Please try again.';
      
      if (error.code === 'functions/already-exists') {
        errorMessage = 'An account with this email already exists. Please try logging in instead.';
      } else if (error.code === 'functions/invalid-argument') {
        errorMessage = error.message || 'Please check your information and try again.';
      }
      
      onError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="email-collection-form-container">
      <div className="form-header">
        <h2 className="text-3xl font-semibold tracking-tight text-gray-900 dark:text-white sm:text-4xl">Join the Waitlist</h2>
        <p className="text-gray-600 dark:text-gray-300">The old way of networking is broken. Join the waitlist for access to Burnt and connect with your fellow top performers.</p>
      </div>
      
      <form onSubmit={handleSubmit} className="email-collection-form">
        <div className="form-group">
          <label htmlFor="email" className="form-label">
            Email Address
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            placeholder="Enter your email address"
            className={`form-input ${formErrors.email ? 'error' : ''}`}
            disabled={isSubmitting}
            autoComplete="email"
            autoFocus
          />
          {formErrors.email && (
            <div className="error-message" role="alert">
              {formErrors.email}
            </div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="linkedinUrl" className="form-label">
            LinkedIn Profile
          </label>
          <input
            type="url"
            id="linkedinUrl"
            name="linkedinUrl"
            value={formData.linkedinUrl}
            onChange={handleInputChange}
            placeholder="https://www.linkedin.com/in/your-profile"
            className={`form-input ${formErrors.linkedinUrl ? 'error' : ''}`}
            disabled={isSubmitting}
            required
          />
          {formErrors.linkedinUrl && (
            <div className="error-message" role="alert">
              {formErrors.linkedinUrl}
            </div>
          )}
          <p className="form-help-text">
            Don't know your URL?{' '}
            <a 
              href="https://www.linkedin.com/in/edit/contact-info/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="form-help-link"
            >
              Find it here
            </a>
          </p>
        </div>
        
        <button
          type="submit"
          disabled={isSubmitting || !formData.email.trim() || !formData.linkedinUrl.trim()}
          className="submit-button"
        >
          {isSubmitting ? 'Sending Verification...' : 'Continue'}
        </button>
        
        <p className="form-note">
          We'll send you a verification code to confirm your email address, then collect your name and password.
        </p>
        
        <p className="mt-6 text-center text-sm text-gray-500 dark:text-gray-400">
          By continuing, you agree to our{' '}
          <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 font-medium underline">
            Terms of Service
          </a>
          {' '}and{' '}
          <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 font-medium underline">
            Privacy Policy
          </a>
        </p>
      </form>
    </div>
  );
};

export default EmailCollectionForm;